import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T, formatClock } from "../constants";
import { useUi, cx } from "../ui";
import { isBookedAhead, vehicleLabel } from "../helpers";
import Plate from "../components/Plate";
import VipBadge from "../components/VipBadge";

const useStyles = makeStyles({
  scroll: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: T.mutedFg,
    padding: "12px 16px",
    borderBottom: `1px solid ${T.border}`,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 16px",
    borderBottom: `1px solid ${T.border}`,
    verticalAlign: "middle",
  },
  guest: { fontWeight: 600, color: T.fg },
  muted: { fontSize: 12, color: T.mutedFg },
  status: { color: T.primary, fontWeight: 600 },
  unpaid: { fontSize: 12, color: T.danger, fontWeight: 600 },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  small: { padding: "7px 12px", fontSize: 12 },
  more: {
    border: "none",
    background: "transparent",
    color: T.mutedFg,
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
    padding: "4px 6px",
    "&:hover": { color: T.fg, textDecoration: "underline" },
  },
  empty: {
    padding: "24px 16px",
    fontSize: 13,
    color: T.mutedFg,
    textAlign: "center",
  },
});

/* Where the car is, or where it is going. */
function whereFor(job) {
  if (job.kind === "park") {
    return job.lotName
      ? `To ${job.lotName}`
      : job.arrivedGate ?? "Concierge handoff";
  }

  return [job.lotName, job.bay && `Bay ${job.bay}`].filter(Boolean).join(" · ");
}

function JobRow({ job, disabled, onAction, onOpen }) {
  const s = useStyles();
  const ui = useUi();
  const pickup = job.kind === "pickup";
  const at = formatClock(pickup ? job.requestedAt : job.claimedAt);
  const bookedAhead = pickup && isBookedAhead(job);
  const action = job.action;

  return (
    <tr aria-label={`${job.kind === "pickup" ? "Pickup" : "Park"} ${job.plate}`}>
      <td className={s.td}>
        <Plate size={12}>{job.plate}</Plate>
      </td>

      <td className={s.td}>
        <div className={s.guest}>
          {job.guestName ?? "Unknown registration"}
          {job.vip && (
            <>
              {" "}
              <VipBadge />
            </>
          )}
        </div>
        <div className={s.muted}>{vehicleLabel(job) ?? "Vehicle not recorded"}</div>
      </td>

      <td className={s.td}>
        {pickup ? "Pickup" : "Park"}
        {at && (
          <div className={s.muted}>
            {bookedAhead
              ? `Due ${at}`
              : pickup
              ? `Requested ${at}`
              : `Keys accepted ${at}`}
          </div>
        )}
      </td>

      <td className={s.td}>
        <span className={s.status}>{job.statusLabel}</span>
        {pickup && job.unpaid && (
          <div className={s.unpaid}>Unpaid</div>
        )}
      </td>

      <td className={s.td}>
        {whereFor(job)}
        <div className={s.muted}>
          {job.runnerName ??
            (pickup ? "Requested · Unassigned" : "Unassigned")}
          {job.mine && " (you)"}
        </div>
      </td>

      <td className={cx(s.td, s.actions)}>
        {action && (
          <button
            type="button"
            className={cx(ui.primaryBtn, s.small)}
            disabled={disabled || action.disabled === true}
            title={action.reason ?? undefined}
            onClick={() => onAction(job)}
          >
            {action.label}
          </button>
        )}

        {/* The way past a step whose primary opens something. */}
        {action?.secondary && (
          <button
            type="button"
            className={cx(ui.mutedBtn, s.small)}
            disabled={disabled}
            onClick={() => onAction(job, action.secondary)}
          >
            {action.secondary.label}
          </button>
        )}

        <button type="button" className={s.more} onClick={() => onOpen(job)}>
          More
        </button>
      </td>
    </tr>
  );
}

export default function RunnerQueueTable({ jobs, ...handlers }) {
  const s = useStyles();

  if (jobs.length === 0) {
    return <div className={s.empty}>Queue is clear. Nice work.</div>;
  }

  return (
    <div className={s.scroll}>
      <table className={s.table}>
        <thead>
          <tr>
            <th className={s.th}>Plate</th>
            <th className={s.th}>Guest</th>
            <th className={s.th}>Job</th>
            <th className={s.th}>Status</th>
            <th className={s.th}>Details</th>
            <th className={s.th} />
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} {...handlers} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
